import { Helmet } from '@dr.pogodin/react-helmet';
import { PlusIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { Api, useApiCallable } from '../../../api';
import {
  ConfirmDeleteModal,
  EmptyState,
  PageError,
  PageLoading,
  PageWrap,
  Stack,
  Tabs,
  Text,
} from '../../../components';
import { Color, Style } from '../../../constants';
import { useCompany } from '../../../contexts/ClientDataContext';
import { useMyUserClient } from '../../../hooks';
import AddResourceModal from '../components/AddResourceModal';
import ResourceCard from '../components/ResourceCard';

export default function DesignResourcesPage() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const company = useCompany(companySlug!);
  const myUser = useMyUserClient();
  const canManage = !!myUser.agencyRole;

  const api = Api.DesignResource.useReadCollectionListCompanySub(company.id);
  const callApi = useApiCallable();

  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(
    null
  );
  const [addModal, setAddModal] = React.useState<{
    show: boolean;
    editing: Api.DesignResource.V | null;
  }>({
    show: false,
    editing: null,
  });
  const [confirmResourceDelete, setConfirmResourceDelete] =
    React.useState<Api.DesignResource.V | null>(null);

  if (api.error) return <PageError {...api} />;
  if (!api.data) return <PageLoading />;

  const resources = api.data['hydra:member'];

  // Group by category
  const categories = new Map<string, Api.DesignResource.V[]>();
  for (const r of resources) {
    const cat = r.category ?? 'Other';
    const list = categories.get(cat);
    if (list) {
      list.push(r);
    } else {
      categories.set(cat, [r]);
    }
  }

  const categoryKeys = Array.from(categories.keys());
  const activeCategory = selectedCategory ?? categoryKeys[0] ?? null;
  const filtered = activeCategory ? categories.get(activeCategory) ?? [] : [];

  const tabs = categoryKeys.map((cat) => ({
    key: cat,
    label: cat,
    count: categories.get(cat)?.length ?? 0,
    selected: cat === activeCategory,
    onClick: () => setSelectedCategory(cat),
  }));

  return (
    <PageWrap background={Color.neutral5} noPadding limitWidth="large">
      <Helmet>
        <title>{`Design Resources | ${company.name}`}</title>
      </Helmet>

      <Stack padH={1.5} padV={1.5} gap={1.5}>
        <html.div style={[Style.row, Style.alignCenter, Style.spaceBetween]}>
          <html.div>
            <Text typeface="body.medium" color="neutral100">
              Design
            </Text>
            <Text as="h1" typeface="headline" color="neutral100">
              Resources
            </Text>
          </html.div>
          <html.button
            style={[Style.row, Style.centerBoth, styles.addBtn]}
            onClick={() => setAddModal({ show: true, editing: null })}
          >
            <PlusIcon size={24} color={Color.neutral0} />
          </html.button>
        </html.div>

        {tabs.length > 0 && (
          <html.div style={styles.tabBar}>
            <Tabs tabs={tabs} accentColor={Color.accent} alwaysShowCount />
          </html.div>
        )}

        {filtered.length === 0 ? (
          <EmptyState
            title="No Figma Files"
            subtitle="Figma Files will be shown here."
          />
        ) : (
          <html.div style={styles.grid}>
            {filtered.map((resource) => (
              <ResourceCard
                key={resource.id}
                resource={resource}
                canManage={canManage}
                onEdit={() => setAddModal({ show: true, editing: resource })}
                onDelete={() => setConfirmResourceDelete(resource)}
              />
            ))}
          </html.div>
        )}
      </Stack>
      {addModal.show && (
        <AddResourceModal
          existing={addModal.editing}
          onClose={() => setAddModal({ show: false, editing: null })}
        />
      )}
      {confirmResourceDelete && (
        <ConfirmDeleteModal
          title={`Delete ${confirmResourceDelete.title}`}
          message="Are you sure you want to delete this file? This action can’t be recovered."
          onConfirm={async () => {
            await callApi(`/design-resources/${confirmResourceDelete.id}`, {
              method: 'DELETE',
            });
            setConfirmResourceDelete(null);
            api.invalidate();
          }}
          onClose={() => setConfirmResourceDelete(null)}
        />
      )}
    </PageWrap>
  );
}

const styles = css.create({
  addBtn: {
    width: 40,
    height: 40,
    borderRadius: 100,
    backgroundColor: color.accent,
    borderWidth: 0,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    ':hover': {
      backgroundColor: '#a3292a',
    },
  },
  tabBar: {
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: {
      default: 'repeat(3, 1fr)',
      '@media (max-width: 768px)': '1fr',
    },
    gap: 24,
  },
});
